import React, { useState, useMemo } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import Table from "../common/Table";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useTemplatesInfinite } from "../../hooks/useTemplatesInfinite";
import {
  Template,
  TemplateFormData,
  TemplateUpdateFormData,
  TemplateRequest,
} from "../../schemas/templateSchema";
import TemplateForm from "../forms/TemplateForm";

const Templates: React.FC = () => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<
    Template | undefined
  >();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [search, setSearch] = useState("");

  const {
    templates,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createTemplate,
    updateTemplate,
    deleteTemplates,
  } = useTemplatesInfinite({ search });

  // Memoize initialData to prevent unnecessary form resets
  const initialFormData = useMemo(
    () =>
      selectedTemplate
        ? {
            name: selectedTemplate.name,
            type: selectedTemplate.type as "image" | "video",
            description: selectedTemplate.description,
            categoryId: selectedTemplate.categoryId,
          }
        : undefined,
    [selectedTemplate]
  );

  const handleEdit = (template: Template) => {
    setSelectedTemplate(template);
    setIsFormModalOpen(true);
  };

  const handleDelete = async (template: Template) => {
    if (window.confirm(`Delete Template "${template.name}"?`)) {
      await deleteTemplates([template.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (window.confirm(`Are you sure you want to delete ${ids.length} template(s)?`)) {
      deleteTemplates(ids);
      setRowSelection({});
    }
  };

  const handleSubmit = async (
    data: TemplateFormData | TemplateUpdateFormData
  ) => {
    setIsSubmitting(true);
    try {
      if (selectedTemplate?.id) {
        await updateTemplate({
          id: selectedTemplate.id,
          template: data as TemplateUpdateFormData,
        });
      } else {
        const createData = data as TemplateFormData;
        const payload: TemplateRequest = {
          name: createData.name || "",
          type: createData.type || ("image" as const),
          description: createData.description,
          categoryId: createData.categoryId || "",
          zipFile:
            createData.zipFile && createData.zipFile.length > 0
              ? createData.zipFile[0]
              : undefined,
          previewFile:
            createData.previewFile && createData.previewFile.length > 0
              ? createData.previewFile[0]
              : undefined,
        };
        await createTemplate(payload);
      }
    } finally {
      setSelectedTemplate(undefined);
      setIsFormModalOpen(false);
      setIsSubmitting(false);
    }
  };

  const columns: ColumnDef<Template>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "type", header: "Type" },
    {
      accessorKey: "category",
      header: "Category",
      cell: ({ getValue }) => {
        const category = getValue() as Template["category"];
        return category?.name || "N/A";
      },
    },
    { accessorKey: "description", header: "Description" },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
    },
  ];

  return (
    <div className="flex-1 m-4 p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Templates</h1>
        <button
          onClick={() => {
            setSelectedTemplate(undefined);
            setIsFormModalOpen(true);
          }}
          className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>

      <Table
        columns={columns}
        data={templates as Template[]}
        actions={actions}
        isLoading={isLoading}
        pagination
        searchable
        infinite
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        onSearch={setSearch}
        selectable
        getRowId={(template) => template.id}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        bulkActions={[
          {
            label: "Delete Selected",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: handleBulkDelete,
            variant: "danger" as const,
          },
        ]}
      />

      {isFormModalOpen && (
        <TemplateForm
          isOpen={isFormModalOpen}
          onCancel={() => {
            setIsFormModalOpen(false);
            setSelectedTemplate(undefined);
          }}
          initialData={initialFormData}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          isUpdate={!!selectedTemplate}
          existingZipFileName={
            selectedTemplate?.zipUrl
              ? selectedTemplate.zipUrl.split("/").pop() || undefined
              : undefined
          }
          existingPreviewFileName={
            selectedTemplate?.previewUrl
              ? selectedTemplate.previewUrl.split("/").pop() || undefined
              : undefined
          }
        />
      )}
    </div>
  );
};

export default Templates;
